import React, {useCallback, useEffect, useState} from 'react';
import ManagementCompany from '../artifacts/src/contracts/ManagementCompany.sol/ManagementCompany'
import {useWeb3React} from "@web3-react/core";
import styled, {css} from 'styled-components/macro'
import Spinner from "./Spinner";
import {AnimatePresence, motion} from "framer-motion";
import CreateProposalModal from "./CreateProposalModal";
import ProposalViewModal from "./ProposalViewModal";
import Footer from "./Footer";

const DashboardPage = styled.div `
    display: flex;
    flex-direction: column;
    width: 100%;
    font-family: Lato;
    font-style: normal;
    z-index:2;
    min-height: 100vh;
`

const SubTitle = styled.div`
    width: 80%;
    height: 25px;
    margin-left: 10%;
    margin-top: 116px;
    margin-bottom: 20px;
    padding: 0px;

    font-weight: bold;
    font-size: 14px;
    line-height: 180%;
    color: #000000;
    
    opacity: 0.4;
`

const Title = styled.div`
    height: 54px;

    font-weight: bold;
    font-size: 30px;
    line-height: 180%;
    /* identical to box height, or 54px */

    text-transform: capitalize;

    color: #000000;
`

const FlexContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0px;

    width: 80%;
    min-width: 1444px;
    margin-left: 10%;
    margin-top: 14px;
`

const ContentContainer = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    margin-top: 34px;
    flex-wrap: wrap;
`

const TableContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0px;
    z-index: 2;

    width: 49%;
    min-width: 533px;
`

const VotingThresholdBox = styled(motion.button)`
    display: flex;
    justify-content: center;

    background: linear-gradient(95.76deg, #FAC44E -0.92%, #F9AB12 96.25%);
    width: 45%;
    min-width: 502px;
    height: 500px;
    border-radius: 6px;
    border: none;
    margin-left: 86px;
`

const VotingThresholdLabel = styled.div`
    width: 100%;
    margin-top: 160px;

    height: 72px;
    font-size: 50px;
    font-weight: bold;
    line-height: 180%;
    
    color: #FFFFFF;
`

const GapArea = styled.div`
    height: 60px;
`

const StyledTable = styled.table`
  border: none;
  border-collapse: separate;
  border-spacing: 0px 20px;
  width: 100%;
  min-width: 533px;
  font-weight: bold;

  thead {
    width: 40%;
    min-width: 533px;
    font-weight: bold;
    font-size: 20px;
    line-height: 180%;
    text-decoration: underline;
  }
    
  td, th {
    min-width: 400px;
    width: 80%;
    left: 36px;

    border: none;
    min-width: 250px;
    text-align: left;
    padding: 0px 50px;
  }  

  td {
    text-align: left;
  }
  
  td #text {
      display: inline-block;
      margin-left: 20px;
      vertical-align: 65%;
  }

  tbody tr {
    width: 100%;
    height: 66px;
    
    vertical-align: middle;
    
    background: #FFFFFF;
    box-shadow: 4px 6px 20px rgba(176, 176, 176, 0.15);
 
    :hover {
      background-color: #f5f5f5;
    }
  }
`;

const AddressZero = '0x0000000000000000000000000000000000000000';

const MCAdminPage = (() => {
    const web3Context = useWeb3React();
    const [ManagementCompanySC] = useState({
        address: process.env.REACT_APP_MCADMIN_ADDRESS,
        abi: ManagementCompany.abi
    });
    const [boardMembers, setBoardMembers] = useState([]);
    const [minApprovalRequired, setMinApprovalRequired] = useState(0);
    const [addMemberProposal, setAddMemberProposal] = useState("");
    const [removeMemberProposal, setRemoveMemberProposal] = useState("");
    const [voteThresholdProposal, setVoteThresholdProposal] = useState(0);
    const [SPVWalletAddressProposal, setSPVWalletAddressProposal] = useState("");
    const [LOSCAddressProposal, setLOSCAddressProposal] = useState("");
    const [LRSCAddressProposal, setLRSCAddressProposal] = useState("");
    const [newMemberVotingFlags, setNewMemberVotingFlags] = useState([]);
    const [removeMemberVotingFlags, setRemoveMemberVotingFlags] = useState([]);
    const [thresholdVotingFlags, setThresholdVotingFlags] = useState([]);
    const [SPVWalletAddressVotingFlags, setSPVWalletAddressVotingFlags] = useState([]);
    const [LOSCAddressVotingFlags, setLOSCAddressVotingFlags] = useState([]);
    const [LRSCAddressVotingFlags, setLRSCAddressVotingFlags] = useState([]);
    const [currentLRSCAddress, setCurrentLRSCAddress] = useState("");
    const [currentLOSCAddress, setCurrentLOSCAddress] = useState("");
    const [currentSPVAddress, setCurrentSPVAddress] = useState("");


    const loadBoardMembers = useCallback(async() => {
        const ethLibrary = web3Context.library.eth;
        const ManagementCompanyContract = new ethLibrary.Contract(
            ManagementCompanySC.abi,
            ManagementCompanySC.address
        );

        const activeBoardMembers = await ManagementCompanyContract.methods
            .getActiveMCBoard()
            .call();

        setBoardMembers(activeBoardMembers.slice(1));
    }, [web3Context, ManagementCompanySC]);

    const loadAdminProposals = useCallback(async() => {
        const ethLibrary = web3Context.library.eth;
        const ManagementCompanyContract = new ethLibrary.Contract(
            ManagementCompanySC.abi,
            ManagementCompanySC.address
        );

        const currentLOSC = await ManagementCompanyContract.methods
            .LOSCAddress()
            .call();
        setCurrentLOSCAddress(currentLOSC);

        const currentLRSC = await ManagementCompanyContract.methods
            .LRSCAddress()
            .call();
        setCurrentLRSCAddress(currentLRSC);

        const currentSPV = await ManagementCompanyContract.methods
            .SPVWalletAddress()
            .call();
        setCurrentSPVAddress(currentSPV);

        const pendingMCBoardMember = await ManagementCompanyContract.methods
            .pendingMCBoardMember()
            .call();
        setAddMemberProposal(pendingMCBoardMember);

        const pendingMinApprovalRequired = await ManagementCompanyContract.methods
            .pendingMinApprovalRequired()
            .call();
        setVoteThresholdProposal(pendingMinApprovalRequired);

        const pendingRemoveMember = await ManagementCompanyContract.methods
            .pendingRemoveMember()
            .call();
        setRemoveMemberProposal(pendingRemoveMember);

        const pendingSPVWallet = await ManagementCompanyContract.methods
            .pendingSPVWalletAddress()
            .call();
        setSPVWalletAddressProposal(pendingSPVWallet);

        const pendingLOSCAddressProposal = await ManagementCompanyContract.methods
            .pendingLOSCAddress()
            .call();
        setLOSCAddressProposal(pendingLOSCAddressProposal);

        const pendingLRSCAddressProposal = await ManagementCompanyContract.methods
            .pendingLRSCAddress()
            .call();
        setLRSCAddressProposal(pendingLRSCAddressProposal);

        const newMemberVotingFlags = await ManagementCompanyContract.methods
            .getBoardMemberVoteFlags()
            .call();
        setNewMemberVotingFlags(newMemberVotingFlags);

        const thresholdVotingFlags = await ManagementCompanyContract.methods
            .getMinApprovalRequiredVoteFlags()
            .call();
        setThresholdVotingFlags(thresholdVotingFlags);

        const removeMemberVotingFlags = await ManagementCompanyContract.methods
            .getRemoveMemberVoteFlags()
            .call();
        setRemoveMemberVotingFlags(removeMemberVotingFlags);

        const SPVWalletAddressVotingFlags = await ManagementCompanyContract.methods
            .getSPVWalletAddressVoteFlags()
            .call();
        setSPVWalletAddressVotingFlags(SPVWalletAddressVotingFlags);

        const LOSCAddressVotingFlags = await ManagementCompanyContract.methods
            .getLOSCAddressVoteFlags()
            .call();
        setLOSCAddressVotingFlags(LOSCAddressVotingFlags);

        const LRSCAddressVotingFlags = await ManagementCompanyContract.methods
            .getLRSCAddressVoteFlags()
            .call();
        setLRSCAddressVotingFlags(LRSCAddressVotingFlags);

        // load current voting threshold
        const votingThreshold = await ManagementCompanyContract.methods
            .minApprovalRequired()
            .call();
        setMinApprovalRequired(votingThreshold);

    }, [web3Context, ManagementCompanySC]);

    const proposeNewAdmin = useCallback(async(newMemberAddress) => {
        const ethLibrary = web3Context.library.eth;
        const ManagementCompanyContract = new ethLibrary.Contract(
            ManagementCompanySC.abi,
            ManagementCompanySC.address
        );

        await ManagementCompanyContract.methods
            .proposeNewAdmin(newMemberAddress.toString())
            .send({from: web3Context.account})
            .once('receipt', async() => {
                window.location.reload();
            });
    }, [web3Context, ManagementCompanySC]);

    const proposeRemoveAdmin = useCallback(async(boardMemberAddress) => {
        const ethLibrary = web3Context.library.eth;
        const ManagementCompanyContract = new ethLibrary.Contract(
            ManagementCompanySC.abi,
            ManagementCompanySC.address
        );

        await ManagementCompanyContract.methods
            .proposeRemoveAdmin(boardMemberAddress.toString())
            .send({from: web3Context.account})
            .once('receipt', async() => {
                window.location.reload();
            });
    }, [web3Context, ManagementCompanySC]);

    const proposeNewApprovalRequiredNumber = useCallback(async(newThreshold) => {
        const ethLibrary = web3Context.library.eth;
        const ManagementCompanyContract = new ethLibrary.Contract(
            ManagementCompanySC.abi,
            ManagementCompanySC.address
        );

        await ManagementCompanyContract.methods
            .proposeNewApprovalRequiredNumber(newThreshold)
            .send({from: web3Context.account})
            .once('receipt', async() => {
                window.location.reload();
            });
    }, [web3Context, ManagementCompanySC]);

    const proposeNewSPVWalletAddress = useCallback(async(newWalletAddress) => {
        const ethLibrary = web3Context.library.eth;
        const ManagementCompanyContract = new ethLibrary.Contract(
            ManagementCompanySC.abi,
            ManagementCompanySC.address
        );

        await ManagementCompanyContract.methods
            .proposeNewSPVWalletAddress(newWalletAddress)
            .send({from: web3Context.account})
            .once('receipt', async() => {
                window.location.reload();
            });
    }, [web3Context, ManagementCompanySC]);

    const proposeNewLOSCAddress = useCallback(async(newLOSCAddress) => {
        const ethLibrary = web3Context.library.eth;
        const ManagementCompanyContract = new ethLibrary.Contract(
            ManagementCompanySC.abi,
            ManagementCompanySC.address
        );

        await ManagementCompanyContract.methods
            .proposeNewLOSCAddress(newLOSCAddress)
            .send({from: web3Context.account})
            .once('receipt', async() => {
                window.location.reload();
            });
    }, [web3Context, ManagementCompanySC]);

    const proposeNewLRSCAddress = useCallback(async(newLRSCAddress) => {
        const ethLibrary = web3Context.library.eth;
        const ManagementCompanyContract = new ethLibrary.Contract(
            ManagementCompanySC.abi,
            ManagementCompanySC.address
        );

        await ManagementCompanyContract.methods
            .proposeNewLRSCAddress(newLRSCAddress)
            .send({from: web3Context.account})
            .once('receipt', async() => {
                window.location.reload();
            });
    }, [web3Context, ManagementCompanySC]);

    const approveNewAdmin = useCallback(async() => {
        const ethLibrary = web3Context.library.eth;
        const ManagementCompanyContract = new ethLibrary.Contract(
            ManagementCompanySC.abi,
            ManagementCompanySC.address
        );

        await ManagementCompanyContract.methods
            .voteNewAdmin()
            .send({from: web3Context.account})
            .once('receipt', async() => {
                window.location.reload();
            });
    }, [web3Context, ManagementCompanySC]);

    const approveNewApprovalRequiredNumber = useCallback(async() => {
        const ethLibrary = web3Context.library.eth;
        const ManagementCompanyContract = new ethLibrary.Contract(
            ManagementCompanySC.abi,
            ManagementCompanySC.address
        );

        await ManagementCompanyContract.methods
            .voteNewApprovalRequiredNumber()
            .send({from: web3Context.account})
            .once('receipt', async() => {
                window.location.reload();
            });
    }, [web3Context, ManagementCompanySC]);

    const approveAdminRemoval = useCallback(async() => {
        const ethLibrary = web3Context.library.eth;
        const ManagementCompanyContract = new ethLibrary.Contract(
            ManagementCompanySC.abi,
            ManagementCompanySC.address
        );

        await ManagementCompanyContract.methods
            .voteRemoveAdmin()
            .send({from: web3Context.account})
            .once('receipt', async() => {
                window.location.reload();
            });
    }, [web3Context, ManagementCompanySC]);

    const approveNewSPVWalletAddress = useCallback(async() => {
        const ethLibrary = web3Context.library.eth;
        const ManagementCompanyContract = new ethLibrary.Contract(
            ManagementCompanySC.abi,
            ManagementCompanySC.address
        );

        await ManagementCompanyContract.methods
            .voteNewSPVWalletAddress()
            .send({from: web3Context.account})
            .once('receipt', async() => {
                window.location.reload();
            });
    }, [web3Context, ManagementCompanySC]);

    const approveNewLOSCAddress = useCallback(async() => {
        const ethLibrary = web3Context.library.eth;
        const ManagementCompanyContract = new ethLibrary.Contract(
            ManagementCompanySC.abi,
            ManagementCompanySC.address
        );

        await ManagementCompanyContract.methods
            .voteNewLOSCAddress()
            .send({from: web3Context.account})
            .once('receipt', async() => {
                window.location.reload();
            });
    }, [web3Context, ManagementCompanySC]);

    const approveNewLRSCAddress = useCallback(async() => {
        const ethLibrary = web3Context.library.eth;
        const ManagementCompanyContract = new ethLibrary.Contract(
            ManagementCompanySC.abi,
            ManagementCompanySC.address
        );

        await ManagementCompanyContract.methods
            .voteNewLRSCAddress()
            .send({from: web3Context.account})
            .once('receipt', async() => {
                window.location.reload();
            });
    }, [web3Context, ManagementCompanySC]);

    useEffect(() => {
        if(web3Context.active) {
            loadBoardMembers();
            loadAdminProposals();
        }
    },[web3Context, ManagementCompanySC, loadBoardMembers, loadAdminProposals]);

    return (
        <AnimatePresence>
            {
                web3Context.account === undefined ? (
                    <DashboardPage>
                        <Spinner/>
                    </DashboardPage>) : (<DashboardPage>
                        <SubTitle>Management > Manage Board Members
                        <br/>MCAdmin Wallet Address: {process.env.REACT_APP_MCADMIN_ADDRESS}</SubTitle>
                        <FlexContainer>
                            <Title>Current Board Members</Title>
                            <ContentContainer>
                                <TableContainer>
                                    <StyledTable>
                                        <thead>
                                        <tr>
                                            <th>
                                                Management Address
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {web3Context.account === undefined ? (<Spinner/>) : boardMembers.map((member, index) => {
                                            return (
                                                <tr>
                                                    <td>{member}</td>
                                                </tr>
                                            )
                                        })}
                                        </tbody>
                                    </StyledTable>
                                </TableContainer>
                                <VotingThresholdBox
                                    whileHover={{scale: 1.1}}>
                                    <VotingThresholdLabel>
                                        Voting Threshold
                                        <br/>
                                        {minApprovalRequired}
                                    </VotingThresholdLabel>
                                </VotingThresholdBox>
                            </ContentContainer>
                        </FlexContainer>
                        <FlexContainer>
                            <Title>Create New Proposal</Title>
                            <ContentContainer>
                                <CreateProposalModal
                                    header = "Add Member Proposal"
                                    label = "Add Member"
                                    note = "Note: When creating a Add Member Proposal, it will replace the existing Add Member Proposal."
                                    action = {proposeNewAdmin}
                                    boardMembers = {boardMembers}
                                />
                                <CreateProposalModal
                                    header = "Remove Member Proposal"
                                    label = "Remove Member"
                                    note = "Note: When creating a Remove Member Proposal, it will replace the existing Remove Member Proposal."
                                    action = {proposeRemoveAdmin}
                                    boardMembers = {boardMembers}
                                />
                                <CreateProposalModal
                                    header = "Change Voting Threshold Proposal"
                                    label = "Change Voting Threshold"
                                    note = "Note: When creating a Change Voting Threshold Proposal, it will replace the existing Change Voting Threshold Proposal."
                                    action = {proposeNewApprovalRequiredNumber}
                                    boardMembers = {boardMembers}
                                />
                                <CreateProposalModal
                                    header = "Change SPV Wallet Proposal"
                                    label = "Change SPV Wallet"
                                    note = "Note: When creating a Change SPV Wallet Proposal, it will replace the existing SPV Wallet Proposal."
                                    action = {proposeNewSPVWalletAddress}
                                    boardMembers = {boardMembers}
                                    currentAddress = {currentSPVAddress}
                                />
                                <CreateProposalModal
                                    header = "Change Loan Originator Address Proposal"
                                    label = "Change Loan Originator"
                                    note = "Note: When creating a Change Loan Originator Proposal, it will replace the existing Loan Originator Proposal."
                                    action = {proposeNewLOSCAddress}
                                    boardMembers = {boardMembers}
                                    currentAddress = {currentLOSCAddress}
                                />
                                <CreateProposalModal
                                    header = "Change Land Registration Address Proposal"
                                    label = "Change Land Registration"
                                    note = "Note: When creating a Change Land Registration Proposal, it will replace the existing Land Registration Proposal."
                                    action = {proposeNewLRSCAddress}
                                    boardMembers = {boardMembers}
                                    currentAddress = {currentLRSCAddress}
                                />
                            </ContentContainer>
                        </FlexContainer>
                        <GapArea/>
                        <FlexContainer>
                            <Title>Current Proposal</Title>
                            <ContentContainer>
                                {
                                    addMemberProposal === AddressZero ? ("") : (
                                        <ProposalViewModal
                                            boxLabel = {"Add Member " + addMemberProposal.substring(0,7)+ "..." + addMemberProposal.substring(addMemberProposal.length - 6)}
                                            action = {approveNewAdmin}
                                            header = "Add New Member Proposal"
                                            boardMembers = {boardMembers}
                                            votingResults = {newMemberVotingFlags}
                                            caller={web3Context.account}
                                        />
                                    )
                                }
                                {
                                    removeMemberProposal === AddressZero ? ("") : (
                                        <ProposalViewModal
                                            boxLabel = {"Remove Member " + removeMemberProposal.substring(0,7)+ "..." + removeMemberProposal.substring(removeMemberProposal.length - 6)}
                                            action = {approveAdminRemoval}
                                            header = "Remove Member Proposal"
                                            boardMembers = {boardMembers}
                                            votingResults = {removeMemberVotingFlags}
                                            caller={web3Context.account}
                                        />
                                    )
                                }
                                {
                                    voteThresholdProposal.toString() === "0" ? ("") : (
                                        <ProposalViewModal
                                            boxLabel = {"Change Voting Threshold to " + voteThresholdProposal.toString()}
                                            action = {approveNewApprovalRequiredNumber}
                                            header = "Change Voting Threshold Proposal"
                                            boardMembers = {boardMembers}
                                            votingResults = {thresholdVotingFlags}
                                            caller={web3Context.account}
                                        />
                                    )
                                }
                                {
                                    SPVWalletAddressProposal.toString() === AddressZero ? ("") : (
                                        <ProposalViewModal
                                            boxLabel = {"Change SPV Wallet Address to " + SPVWalletAddressProposal.substring(0,7)+ "..." + SPVWalletAddressProposal.substring(SPVWalletAddressProposal.length - 6)}
                                            action = {approveNewSPVWalletAddress}
                                            header = "Change SPV Wallet Proposal"
                                            boardMembers = {boardMembers}
                                            votingResults = {SPVWalletAddressVotingFlags}
                                            caller={web3Context.account}
                                        />
                                    )
                                }
                                {
                                    LOSCAddressProposal.toString() === AddressZero ? ("") : (
                                        <ProposalViewModal
                                            boxLabel = {"Change Loan Originator Address to " +  LOSCAddressProposal.substring(0,7)+ "..." + LOSCAddressProposal.substring(LOSCAddressProposal.length - 6)}
                                            action = {approveNewLOSCAddress}
                                            header = "Change Loan Originator Proposal"
                                            boardMembers = {boardMembers}
                                            votingResults = {LOSCAddressVotingFlags}
                                            caller={web3Context.account}
                                        />
                                    )
                                }
                                {
                                    LRSCAddressProposal.toString() === AddressZero ? ("") : (
                                        <ProposalViewModal
                                            boxLabel = {"Change Land Registration Address to " +  LRSCAddressProposal.substring(0,7)+ "..." + LRSCAddressProposal.substring(LRSCAddressProposal.length - 6)}
                                            action = {approveNewLRSCAddress}
                                            header = "Change Land Registration Proposal"
                                            boardMembers = {boardMembers}
                                            votingResults = {LRSCAddressVotingFlags}
                                            caller={web3Context.account}
                                        />
                                    )
                                }
                            </ContentContainer>
                        </FlexContainer>
                    </DashboardPage>
                )
            }
        </AnimatePresence>
    );
});

export default MCAdminPage;
